import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA, ViewEncapsulation, effect, computed } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatRippleModule, ThemePalette } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { authService } from 'src/app/services/auth.service';
import { userDataService } from 'src/app/services/userDataService';
import { FirebaseService } from 'src/app/services/firebase.service';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { ToDoListComponent } from 'src/app/shared/to-do-list/to-do-list.component';
import { MatDialog } from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';
import { tasksServices } from 'src/app/services/tasks.service';
import { EventConfirmationDialogComponent } from 'src/app/shared/calendar/event-confirmation-dialog/event-confirmation-dialog.component';
import { NotificationTypeEnum } from 'src/app/leads/interface-data/data';
import { DialogComponent } from 'src/app/shared/dialog/confirm.dialog';
import { UnifiedNotificationService } from 'src/app/services/unified-notification.service';
import { SocketService } from 'src/app/services/socket.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UserAction, UserActionService, EngagementHubStats } from 'src/app/services/user-action.service';
import { UserActionsDialogComponent } from 'src/app/shared/user-actions-dialog/user-actions-dialog.component';
import { GlobalSearchComponent } from 'src/app/shared/global-search/global-search.component';
import { debounceTime } from 'rxjs/operators';
import { MyGoalsDialogComponent } from '../shared/my-goals-dialog';
import { HelpPanelDialogComponent } from 'src/app/shared/help-panel/help-panel.component';
import { SmsProviderService } from 'src/app/services/sms-provider.service';
import { RcConnectPromptComponent } from 'src/app/shared/rc-connect-prompt/rc-connect-prompt.component';

interface NavItem {
  navCap?: string;
  displayName?: string;
  iconName?: string;
  route?: string;
  roleStrict: string[];
  children?: NavItem[];
  hideFromNav?: boolean; // Added property to control visibility in navigation
}

interface user {
  id: number;
  name: string;
  lastName: string;
  email: string;
  cloud_key:string;
}

interface Profile {
  id: number;
  title: string;
  link: string;
  roles: string[];
  icon?: string; // Optional icon for menu items
}

@Component({
  selector: 'app-full',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatListModule,
    MatBadgeModule,
    MatDividerModule,
    MatRippleModule,
    MatTooltipModule,
    MatExpansionModule,
    TablerIconsModule,
    GlobalSearchComponent, // Add this line
  ],
  templateUrl: './full.component.html',
  styleUrls: ['./full.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [
    trigger('pulseAnimation', [
      state('pulse', style({
        transform: 'scale(1.1)'
      })),
      state('normal', style({
        transform: 'scale(1)'
      })),
      transition('normal => pulse', animate('300ms ease-in')),
      transition('pulse => normal', animate('300ms ease-out'))
    ])
  ],
})
export class FullComponent implements OnInit {
  isAdminExpanded = false; // Start with admin sidebar collapsed
  currentUserRole = this.authService.getUserRole(); // This should be fetched from auth service
  pathImage:string;
  userProfile: any;
  notifications: any[] = [];
  currentTab: string = 'unread';
  pageIndex = 1;
  total_notifications: number = 0;
  userName:string;
  notificationType: 'notification' | 'inbox' = 'notification'; // Track which menu is open
  animationState = 'normal';

  // Engagement Hub Stats
  engagementHubStats: EngagementHubStats | null = null;
  engagementHubTotal: number = 0;
  hasNewEngagementItems: boolean = false;

  profile: Profile[] = [
    {
      id: 1,
      title: 'My Profile',
      link: '/settings/profile',
      roles: ['ADMIN', 'USER', 'LEAD'],
      icon: 'person'
    },
    {
      id: 2,
      title: 'Forms',
      link: '/forms',
      roles: ['ADMIN','USER'],
      icon: 'wysiwyg'
    },
    {
      id: 2,
      title: 'Company Settings',
      link: '/settings/profile/company',
      roles: ['ADMIN'],
      icon: 'business'
    },
    {
      id: 3,
      title: 'Goal',
      link: '/goals',
      roles: ['ADMIN'],
      icon: 'star_border_purple500'
    },
  ];
  
  navItems: NavItem[] = [
    // Admin Section
    {
      navCap: 'Admin',
      roleStrict: ['ADMIN', 'USER'],
    },
    {
      displayName: 'Admin Config',
      iconName: 'admin_panel_settings',
      route: '/admin-config',
      roleStrict: ['ADMIN', 'USER'],
      children: [
        {
          displayName: 'Campus',
          iconName: 'business',
          route: '/admissions/campus',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Programs',
          iconName: 'school',
          route: '/admissions/programs',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Pipelines',
          iconName: 'account_tree',
          route: '/admissions/pipelines',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Documents',
          iconName: 'folder',
          route: '/admissions/documents',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Applications',
          iconName: 'assignment',
          route: '/admissions/applications',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Labels',
          iconName: 'label_outline',
          route: '/admissions/labels',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Campaigns',
          iconName: 'campaign',
          route: '/admissions/campaigns',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Templates',
          iconName: 'description',
          route: '/admissions/templates',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Layouts',
          iconName: 'dashboard',
          route: '/admissions/layouts',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Automations',
          iconName: 'autorenew',
          route: '/admissions/automations',
          roleStrict: ['ADMIN', 'USER'],
        },
        {
          displayName: 'Users',
          iconName: 'person',
          route: '/admissions/users',
          roleStrict: ['ADMIN', 'USER'],
        },
      ],
    },
    
    // Workflow Section
    {
      navCap: 'Workflow',
      roleStrict: ['ADMIN', 'USER'],
    },
   
    {
      displayName: 'Dashboard',
      iconName: 'home',
      route: 'admissions/dashboard',
      roleStrict: ['ADMIN', 'USER'],
    },
    {
      displayName: 'Engagement Hub',
      iconName: 'hub',
      route: '/admissions/engagment-hub',
      roleStrict: ['ADMIN', 'USER'],
    },

    {
      displayName: 'Leads',
      iconName: 'person_search',
      route: '/leads/leads-managment',
      roleStrict: ['ADMIN', 'USER'],
    },
    // {
    //   displayName: 'Pipeline',
    //   iconName: 'timeline',
    //   route: '/lead-pipeline',
    //   roleStrict: ['ADMIN', 'USER'],
    // },
    {
      displayName: 'Activities',
      iconName: 'event_note',
      route: '/activities',
      roleStrict: ['ADMIN', 'USER'],
    },
    
    // Reports Section - Now as separate items in root menu
    {
      navCap: 'Reports',
      roleStrict: ['ADMIN', 'USER'],
    },
    {
      displayName: 'General Reports',
      iconName: 'bar_chart',
      route: '/reports/general',
      roleStrict: ['ADMIN', 'USER'],
    },
    // Uncomment if you want to add Custom Reports
    // {
    //   displayName: 'Custom Reports',
    //   iconName: 'analytics',
    //   route: '/reports/custom',
    //   roleStrict: ['ADMIN', 'USER'],
    // },
    
    // Student Hub Section
    {
      navCap: 'Student Hub',
      roleStrict: ['LEAD'],
    },
    {
      displayName: 'Notifications',
      iconName: 'notifications',
      route: '/portal/notification',
      roleStrict: ['LEAD'],
    },
    {
      displayName: 'Documents',
      iconName: 'folder_open',
      route: '/portal/documents',
      roleStrict: ['LEAD'],
    },
    {
      displayName: 'Applications',
      iconName: 'description',
      route: '/portal/application',
      roleStrict: ['LEAD'],
    },
    {
      displayName: 'Financial aid',
      iconName: 'monetization_on',
      route: '/portal/financial-aid',
      roleStrict: ['LEAD'],
    },
    
    // Hidden routes that should be accessible but not shown in navigation
    {
      displayName: 'User Profile',
      iconName: 'person',
      route: '/settings/profile',
      roleStrict: ['ADMIN', 'USER', 'LEAD'],
      hideFromNav: true,
    },
    {
      displayName: 'Company Profile',
      iconName: 'business',
      route: '/settings/profile/company',
      roleStrict: ['ADMIN', 'USER'],
      hideFromNav: true,
    },
    {
      displayName: 'All Notifications',
      iconName: 'notifications',
      route: '/leads/notifications',
      roleStrict: ['ADMIN', 'USER'],
      hideFromNav: true,
    },
    {
      displayName: 'Inbox',
      iconName: 'mail',
      route: '/inbox',
      roleStrict: ['ADMIN', 'USER'],
      hideFromNav: true,
    },
  ];

  // Computed signal to get the company logo URL
  companyLogoUrl = computed(() => {
    const userData = this.userService.getUserDataSignal()();
    const companyLogo = userData?.Company?.companyLogo;

    if (companyLogo) {
      return `${environment.publicCloudFlareUrl}/company-logo/thumb_${companyLogo}`;
    }

    // Fallback to default dostifycrm logo
    return '../../../assets/images/logos/dostify-logo.svg';
  });

  constructor(
    public router: Router,
    private authService:authService,
    private userService:userDataService,
    public notificationService:SocketService,
    private dialog:MatDialog,
    private taskService:tasksServices,
    public userActionService: UserActionService,
    private smsProviderService: SmsProviderService,

  ) {
    const userDataSignal = this.userService.getUserDataSignal();

    // Create an effect to respond to changes (Angular 16+)
    effect(() => {
      const userData = userDataSignal();
      this.userProfile = userData;
      this.pathImage = `${environment.publicCloudFlareUrl}/users/thumb_${userData?.cloud_key}`;
      this.userName = `${userData?.name} ${userData?.lastName}`;
    });
  }

  async ngOnInit() {
    this.userProfile = await this.userService.getUserData();

    if(this.currentUserRole !=='LEAD'){
      this.notificationService.initializeAudio()
      // this.notificationService.initialize();
     
      this.getCount()
      this.getInboxCount() // Get inbox count
      this.userActionService.initializeStats()
      
      // Load engagement hub stats
      await this.loadEngagementHubStats();
      
      // Set up real-time updates for engagement hub
      this.setupEngagementHubUpdates();

      // Check if user needs to connect RingCentral
      this.checkRcConnectPrompt();
    }
    this.pathImage = `${environment.publicCloudFlareUrl}/users/thumb_${this.userProfile.cloud_key}`
    this.userName = `${this.userProfile.name} ${this.userProfile.lastName}`

    this.userActionService.newAction$.subscribe(action => {
      this.triggerPulseAnimation();
      
    });
  }

  async loadEngagementHubStats() {
    // Sidebar badge always reflects the logged-in user's own pending count,
    // independent of any filter set on the engagement hub page.
    this.engagementHubStats = await this.userActionService.getEngagementHubStatsV2();
    this.calculateEngagementHubTotal();
  }

  calculateEngagementHubTotal() {
    if (this.engagementHubStats) {
      this.engagementHubTotal = 
        this.engagementHubStats.sections['new-leads'].count +
        this.engagementHubStats.sections['follow-ups'].count +
        this.engagementHubStats.sections['stalled-deals'].count;
    }
  }

  setupEngagementHubUpdates() {
    // Listen for stats updates from the service with debouncing to prevent multiple API calls
    this.userActionService.statsUpdated$
      .pipe(
        debounceTime(500) // Wait 500ms after the last event before making API call
      )
      .subscribe(() => {
        const oldTotal = this.engagementHubTotal;
        this.loadEngagementHubStats().then(() => {
          // Show visual feedback if count increased
          if (this.engagementHubTotal > oldTotal) {
            this.hasNewEngagementItems = true;
            setTimeout(() => {
              this.hasNewEngagementItems = false;
            }, 5000); // Remove after 5 seconds
          }
        });
      });
  }

  // Check if this is the engagement hub item
  shouldShowEngagementBadge(item: NavItem): boolean {
    return item.route === '/admissions/engagment-hub' && 
           this.engagementHubTotal > 0 &&
           this.hasAccess(['ADMIN', 'USER']);
  }

  // Get the badge count for engagement hub
  getEngagementHubBadge(item: NavItem): string {
    if (item.route === '/admissions/engagment-hub') {
      return this.engagementHubTotal > 99 ? '99+' : this.engagementHubTotal.toString();
    }
    return '';
  }

  formatBadgeCount(count: number): string {
    if (count > 99) {
      return '99+';
    }
    return count.toString();
  }

  // Enhanced tooltip to show breakdown
// Simplified tooltip method - just return the display name
getItemTooltip(item: NavItem): string {
  return item.displayName || '';
}

  toggleAdminSidebar() {
    this.isAdminExpanded = !this.isAdminExpanded;
  }

  getUnreadNotificationsCount() {
    return this.notifications.filter(n => !n.read).length;
  }

  markAllAsRead() {
    this.notifications.forEach(n => n.read = true);
  }

  // Check if the user has access to a specific menu item
  hasAccess(roleStrict: string[]): boolean {
    return roleStrict.includes(this.currentUserRole);
  }

  // Check if a menu item has children
  hasChildren(item: NavItem): boolean {
    return item.children && item.children.length > 0;
  }

  // Get visible children of a menu item
  getVisibleChildren(item: NavItem): NavItem[] {
    return item.children ? item.children.filter(child => 
      this.hasAccess(child.roleStrict)
    ) : [];
  }

  // Get all non-admin nav items for the main sidebar
  getMainNavItems(): NavItem[] {
    // Filter out the Admin Config item and only include regular menu items that are not hidden
    return this.navItems.filter(item => 
      ((item.navCap && item.navCap !== 'Admin') || 
      (!item.navCap && !this.hasChildren(item))) && 
      !item.hideFromNav // Filter out items marked as hidden
    );
  }

  // Get admin nav items for the second sidebar
  getAdminNavItems(): NavItem[] {
    // Return only the Admin Config item that has children
    return this.navItems.filter(item => 
      !item.navCap && this.hasChildren(item) && item.displayName === 'Admin Config'
    );
  }

 openToDoSidenav() {
    this.dialog.open(ToDoListComponent, {
      width: '30%',
      height: '100%',
      position: { right: '0', top: '0' },
      panelClass: 'todo-dialog'
    });
  }

  getActiveNavItem(): string {
    // Get current query params
    const queryParams = this.router.parseUrl(this.router.url).queryParams;
    
    // Find the best matching route from all nav items (including hidden ones)
    let bestMatchItem: NavItem = null;
    let bestMatchLength = 0;

    const checkItem = (item: NavItem) => {
      // REMOVE: if(item.route == 'admissions/engagment-hub')
      if (item.route && this.router.url.startsWith(item.route) && item.route.length > bestMatchLength) {
        bestMatchItem = item;
        bestMatchLength = item.route.length;
      }
    };
    
    // Check all items including hidden ones
    for (const item of this.navItems) {
      // Check parent route
      checkItem(item);
      
      // Check child routes
      if (item.children) {
        for (const child of item.children) {
          checkItem(child);
        }
      }
    }
    
    // Also check profile menu items
    for (const item of this.profile) {
      if (item.link && this.router.url.startsWith(item.link) && item.link.length > bestMatchLength) {
        // Found a better match in profile items
        bestMatchLength = item.link.length;
        // Create a temporary NavItem from profile item
        bestMatchItem = {
          displayName: item.title,
          route: item.link,
          roleStrict: item.roles
        };
      }
    }
    
    // Special handling for notifications page with messages view
    if (bestMatchItem && bestMatchItem.route === '/leads/notifications' && queryParams['view'] === 'messages') {
      return 'All Messages';
    }
    
    return bestMatchItem ? bestMatchItem.displayName : 'Dashboard';
  }

  /**
   * Checks if the current route matches or is a child of the given route path
   * This helps to highlight sidebar items when on add/edit pages
   */
  isRouteActive(route: string): boolean {
    // Check if the current URL starts with the route path
    // This will match both exact routes and child routes (like /users/add or /users/edit/123)
    return this.router.url.startsWith(route);
  }

  async getCount() {
    try {
      const response = await firstValueFrom(this.notificationService.getTotalUnseen());
      // Set the notification count using the value from the response
      if (response && response.total !== undefined) {
        this.notificationService.set(response.total);
      }
    } catch (error) {
      console.error('Error fetching unread notification count:', error);
    }
  }

  async getInboxCount() {
    try {
      const response = await firstValueFrom(this.notificationService.getTotalUnseenInbox());
      // Set the inbox count using the value from the response
      if (response && response.total !== undefined) {
        this.notificationService.setInboxCount(response.total);
      }
    } catch (error) {
      console.error('Error fetching unread inbox count:', error);
    }
  }
  
  openNotifications() {
    this.notificationType = 'notification';
    this.pageIndex = 1;
    this.notifications = [];
    this.onLoadData();
  }
  
  onScroll(event: any): void {
    const element = event.target;
  
    if (element.scrollHeight - element.scrollTop === element.clientHeight) {
      if (this.notificationType === 'notification') {
        this.pageIndex++;
        this.onLoadData();
      }
    }
  }
  
  async onLoadData() {
    let params:any = {
      pageIndex: this.pageIndex,
      pageSize: 5,
      sortField: '',
      sortDirection: '',
      searchValue: '',
      showMessage: false, // Add this to filter only messages
    };
    
    if(this.currentTab == "unread"){
      params.seen=false
    }
    
    const response = await firstValueFrom(this.notificationService.get(params));
    
    this.notifications = (this.pageIndex == 1 ? [] : this.notifications).concat(
      response.data.filter(row => row.type !== 'Message').map(row => {
        return {
          id: row.id,
          icon: row.icon,
          color: 'accent',
          time: row.created_at,
          title: row.title,
          subtitle: row.body,
          seen: row.seen,
          isActivity: row.isActivity,
          activity_id: row.activity_id,
          type: row.type,
          lead_id:row.lead_id,
          urlLink:row.urlLink,
          disableLink: row.disableLink
        }
      })
    );
  }

  
  async openNotification(event, notification, isInbox = false) {
    if (!notification.seen) {
      const response = await firstValueFrom(this.notificationService.patch({}, notification.id));
      if (isInbox) {
        this.notificationService.decrementInbox();
      } else {
        this.notificationService.decrement();
      }
      notification.seen = true;
    }


    if(notification.disableLink){
      const header =  notification?.title || 'Notification from Automation module'
      const body = notification?.subtitle
      return await this.showNotificationDialog(header, body, true, '700px', 'info')
    }

    else if(notification.type == NotificationTypeEnum.IMPORT_SUCCSESS || notification.type == NotificationTypeEnum.GROUP_MESSAGE_SUCCESSFULLY){
      const header = notification?.title
      const body = notification?.subtitle
      return await this.showNotificationDialog(header, body, true, '700px', 'info')
    }


   else if(notification.type == NotificationTypeEnum.Activity || notification.type == NotificationTypeEnum.Reminder || notification.type == NotificationTypeEnum.Overdue){

      if(!notification.activity_id){
        await this.showNotificationDialog('Activity Deleted', 'This activity has been deleted.', true);
        return;
    }
    
    this.router.navigate(['/activities/list'], {
      queryParams: { activity_id: notification.activity_id } // Documents tab
    });
      } else if (notification.type == NotificationTypeEnum.IMPORT_FAILED){
        const header = `${notification?.title}`
        const body = `We encountered errors during the import process. Click Download to view the Excel file with highlighted error rows and detailed comments about the issues. Please review and correct the errors before attempting to re-upload.`
        const allowDownload = await this.showNotificationDialog(header, body, false, '700px', 'error', 'Download')
        if(allowDownload){
          const targetUrl = `${environment.publicCloudFlareUrl}/excel-reports/${notification?.urlLink}`;
          window.open(targetUrl, '_blank');
        }
      } else if(notification.type == NotificationTypeEnum.GROUP_MESSAGE_SOME_FAILED){
        const header = `${notification?.title}`
        const body = `Some group messages failed to send. Please click "Download" to view the detailed report with error information.`
        const allowDownload = await this.showNotificationDialog(header, body, false, '700px', 'error', 'Download')
        if(allowDownload){
          const targetUrl = `${environment.publicCloudFlareUrl}/excel-reports/${notification?.urlLink}`;
          window.open(targetUrl, '_blank');
        }
      } else {
      const targetUrl = `/leads/leads-managment/manage/${notification.lead_id}`;
      let tab=0
      if (notification.type === NotificationTypeEnum.UploadDocuments) {
        const user = JSON.parse(localStorage.getItem('userData') || '{}');
        console.log(user?.Company)
        tab = user?.Company?.enable_calls ? 4 : 3;
      }
      
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([targetUrl], {
            queryParams: { section:tab },
            queryParamsHandling: 'merge' // Preserve any other existing query parameters
        });
    });
    }
  }
  
  setTab(event, tab: string): void {
    event.stopPropagation();
    if (this.notificationType === 'notification') {
      this.pageIndex = 1;
      this.notifications = [];
      this.currentTab = tab;
      this.onLoadData();
    } 
  }

  async clearAllNotifications(event: Event) {
    event.stopPropagation();
    
    const body = {
      isSelectAll: true,
      seen: true,
      isMessage: false
    };
  
    const response = await firstValueFrom(this.notificationService.markAsSeen(body));
  
    // Update the count
    this.notificationService.set(response?.unseenCount || 0);
    
    // Clear the notifications array and reload
    this.notifications = [];
    this.pageIndex = 1;
    
    // Reload the data
    this.onLoadData();
  }
  
  goToInbox() {
    this.router.navigate(['/inbox']);
  }
  
  showAllNotifications() {
    this.router.navigate(['/leads/notifications']);
  }


     showNotificationDialog(header: string, content: string, isInfo = false, width = '500px',type='info',buttonName='') {
          const dialogRef = this.dialog.open(DialogComponent, {
              data: {
                  header,
                  content,
                  isInfo,
                  type,
                  buttonName
              },
              width,
          });
  
          return dialogRef.afterClosed().toPromise();
      }

  checkRcConnectPrompt(): void {
    this.smsProviderService.getMyRcNumber().subscribe({
      next: (res) => {
        if (
          res.provider !== 'ringcentral' ||
          res.number?.rc_connected ||
          res.rc_connect_dismissed
        ) {
          return;
        }

        const dialogRef = this.dialog.open(RcConnectPromptComponent, {
          width: '450px',
          disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result === 'connect') {
            this.router.navigate(['/settings/profile'], { queryParams: { tab: 'ringcentral' } });
          } else {
            this.smsProviderService.dismissRcPrompt().subscribe();
          }
        });
      },
    });
  }

  async logout() {
    this.authService.logout()
  }


  getTodoIcon(): string {
    const status = this.userActionService.myActionsTodoStatus();
    switch (status) {
      case 'NOW': return 'warning';
      case 'TODAY': return 'schedule';
      default: return 'check_circle';
    }
  }

  getTodoLabel(): string {
    const status = this.userActionService.myActionsTodoStatus();
    switch (status) {
      case 'NOW': return 'Now';
      case 'TODAY': return 'Today';
      default: return 'Clear';
    }
  }

  getBadgeCount(): string {
    const total = this.userActionService.getMyActionsTotalCount();
    return total > 0 ? total.toString() : '';
  }

  getMinimalIcon(): string {
    const status = this.userActionService.myActionsTodoStatus();
    switch (status) {
      case 'NOW': return 'bolt';     // Exciting, action-oriented
      case 'TODAY': return 'light_mode';      // Positive, bright day ahead
      default: return 'celebration';          // Celebrate being done!
    }
  }
  
  getMinimalText(): string {
    // Check if work queue is enabled from user data
    const userData = this.userService.getUserDataSignal()();
    const isWorkQueueEnabled = userData?.Company?.show_folder_engagement_hub ?? false;

    // When work queue is enabled, always show "Opportunity now!" without numbers
    if (isWorkQueueEnabled) {
      return 'Opportunity now!';
    }

    // When work queue is disabled, show original behavior with counts
    const status = this.userActionService.todoStatus();
    const nowCount = this.userActionService.getNowCount();
    const todayCount = this.userActionService.getTodayCount();

    switch (status) {
      case 'NOW':
        return nowCount === 1 ? 'Opportunity now!' : `${nowCount} live opportunities!`;
      case 'TODAY':
        return todayCount === 1 ? 'Planned ahead' : `${todayCount} planned`;
      default:
        return 'All done! 🎉';
    }
  }
  
  getActionTooltip(): string {
    // Check if work queue is enabled from user data
    const userData = this.userService.getUserDataSignal()();
    const isWorkQueueEnabled = userData?.Company?.show_folder_engagement_hub ?? false;

    // When work queue is enabled, show simple message without numbers
    if (isWorkQueueEnabled) {
      return 'Click to view your actions';
    }

    // When work queue is disabled, show original behavior with counts
    const now = this.userActionService.getNowCount();
    const today = this.userActionService.getTodayCount();

    if (now > 0) {
      return `${now} student${now > 1 ? 's' : ''} ready to hear from you!`;
    } else if (today > 0) {
      return `${today} conversation${today > 1 ? 's' : ''} planned for today`;
    }
    return 'Amazing! You\'re all caught up! 🌟';
  }

  getBadgeColor(): ThemePalette {
    const status = this.userActionService.myActionsTodoStatus();
    switch (status) {
      case 'NOW': return 'warn';
      case 'TODAY': return 'accent';
      default: return 'primary';
    }
  }
  trackByActionId(index: number, action: UserAction): number {
    return action.id;
  }

  getTooltipText(): string {
    // Check if work queue is enabled from user data
    const userData = this.userService.getUserDataSignal()();
    const isWorkQueueEnabled = userData?.Company?.show_folder_engagement_hub ?? false;

    // When work queue is enabled, show simple message without numbers
    if (isWorkQueueEnabled) {
      return 'Click to view your actions';
    }

    // When work queue is disabled, show original behavior with counts
    const now = this.userActionService.getNowCount();
    const today = this.userActionService.getTodayCount();

    if (now > 0) {
      return `${now} urgent action${now > 1 ? 's' : ''} need attention now!`;
    } else if (today > 0) {
      return `${today} action${today > 1 ? 's' : ''} due today`;
    }
    return 'All caught up!';
  }

  triggerPulseAnimation() {
    this.animationState = 'pulse';
    setTimeout(() => {
      this.animationState = 'normal';
    }, 300);
  }

  // Add this method to your component class
getActionCount(): string {
  const status = this.userActionService.todoStatus();
  
  // You can customize these numbers based on your actual data
  switch(status) {
    case 'NOW':
      return '3'; // Number of urgent actions
    case 'TODAY':
      return '5'; // Number of today's actions
    case 'CLEAR':
      return '✓'; // Checkmark when complete
    default:
      return '0';
  }
}

  openUserActions() {
    const dialogRef = this.dialog.open(UserActionsDialogComponent, {
      width: '500px',
      height: '80vh',
      position: { right: '20px', top: '80px' },
      panelClass: 'user-actions-dialog',
      hasBackdrop: true,
      data: {
        initialTab: this.userActionService.myActionsTodoStatus() === 'NOW' ? 'now' : 'today'
      }
    });
  }

  openMyGoals() {
    this.dialog.open(MyGoalsDialogComponent, {
      width: '400px',
      height: '80vh',
      position: { right: '20px', top: '80px' },
      panelClass: 'my-goals-dialog-panel',
      hasBackdrop: true,
    });
  }


  // Add this method (replace toggleHelpPanel and closeHelpPanel if they exist)
openHelpCenter() {
  this.dialog.open(HelpPanelDialogComponent, {
    width: '450px',
    height: '80vh',
    position: { right: '20px', top: '80px' },
    panelClass: 'help-panel-dialog',
    hasBackdrop: true,
    data: {
      currentRoute: this.router.url
    }
  });
}
}